/**
 * 이상한 방 - 봉투 화면의 머리(눈썹 라벨 · 제목 · 핀 칩 · 탄창 칩) (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.3절(0~300ms "핀 칩에서 값이 떨어진다" · 1500~1800ms "탄창이면 상단 탄창 칩으로 날아가 숫자가 오른다")
 *
 * `ui/envelope.ts`에서 갈라져 나왔다. 머리에 있는 것은 전부 **계정의 수**다 - 핀과 탄창. 그 수를 여기서 계산하지 않는다
 * (`pinsChip.ts` 머리말과 같은 결정): 출발값·도착값·떨어지는 값·오르는 값을 전부 화면이 받은 그대로 놓는다.
 * `pinsAfter + spent`로 역산하면 환급이 있는 회차에서 칩이 다른 수를 말한다.
 *
 * 탄창 칩은 핀 칩(`.pc-root`)과 같은 문법이되 더 작다 - 이 화면의 주인공은 핀이다(`envelope.css`).
 */

import { t } from '../i18n'

import { createElement, formatNumber, setText } from './dom'
import { createPinsChip, type PinsChipHandle } from './pinsChip'

import './envelope.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 눈썹 라벨이 말할 회차. `replay`면 "다시 보기 N회차". */
export type EnvelopeHeadView = Readonly<{ seq: number; replay: boolean }>

export type EnvelopeHead = Readonly<{
  /** `.ev-head`. 루트에 붙이는 것은 화면의 몫이다. */
  root: HTMLElement
  /** 핀 칩. 값을 놓고 떨어뜨리는 것은 화면이 시각표에 맞춰 부른다. */
  pins: PinsChipHandle
  /** 제목·탄창 라벨, 그리고 뷰가 있으면 눈썹 라벨. 언어 전환도 이 함수를 다시 부른다. */
  paintTexts: (view: EnvelopeHeadView | null) => void
  /** 탄창 칩을 첫 상태로. 튐을 지우고 열기 **전** 값을 놓는다. */
  resetMagazines: (count: number) => void
  /** 탄창 칩의 수. 스탬프 시각과 컷(탭·움직임 줄이기)에서 열고 난 값이 된다. */
  setMagazines: (count: number) => void
  /** `+N`이 카드 쪽에서 날아와 칩이 튄다. 실제로 들어간 수만 - 넘친 몫은 핀이 되어 카드의 한 줄이 말한다. */
  bumpMagazines: (gained: number) => void
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

/** 머리를 만든다. 붙이지 않는다. */
export function createEnvelopeHead(): EnvelopeHead {
  const head = createElement('header', 'ev-head')
  const eyebrow = createElement('p', 'ev-eyebrow', '')
  const title = createElement('h1', 'ev-title', '')
  // 루트의 `aria-labelledby`가 이 id를 가리킨다.
  title.id = 'ev-title'
  const chips = createElement('div', 'ev-chips')
  const pinsChip = createPinsChip()
  const mags = createElement('div', 'ev-mags')
  mags.setAttribute('role', 'status')
  const magsIcon = createElement('span', 'ev-mags__icon')
  magsIcon.setAttribute('aria-hidden', 'true')
  const magsCount = createElement('strong', 'ev-mags__count', '0')
  const magsLabel = createElement('span', 'ev-mags__label', '')
  const magsGain = createElement('span', 'ev-mags__gain', '')
  magsGain.setAttribute('aria-hidden', 'true')
  mags.append(magsIcon, magsCount, magsLabel, magsGain)
  chips.append(pinsChip.root, mags)
  head.append(eyebrow, title, chips)

  return {
    root: head,
    pins: pinsChip,

    paintTexts(view: EnvelopeHeadView | null): void {
      title.textContent = t('envelope.title')
      magsLabel.textContent = t('envelope.magsChip')
      if (view === null) return
      eyebrow.textContent = view.replay ? t('envelope.replay', { seq: view.seq }) : t('envelope.seq', { seq: view.seq })
    },

    resetMagazines(count: number): void {
      mags.classList.remove('is-bump')
      magsGain.textContent = ''
      setText(magsCount, formatNumber(count))
    },

    setMagazines(count: number): void {
      setText(magsCount, formatNumber(count))
    },

    bumpMagazines(gained: number): void {
      magsGain.textContent = t('envelope.magsGain', { count: gained })
      mags.classList.add('is-bump')
    },

    dispose(): void {
      pinsChip.dispose()
    },
  }
}
